/**
 * Session de l'utilisateur connecté.
 *
 * Deux minuteries, qui ne disent pas la même chose (section 3) :
 *  - la session s'arrête au bout de douze heures, quoi qu'il arrive ;
 *  - trente minutes sans toucher l'écran déconnectent aussi.
 *
 * La seconde est surveillée ici pour prévenir avant de couper : une sage-femme
 * qui revient d'une salle de naissance ne doit pas découvrir un écran de
 * connexion sans comprendre pourquoi.
 */

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { apiFetch, setAccessToken, onSessionLost } from '../lib/api';
import { connectRealtime, disconnectRealtime } from '../lib/socket';

export interface HospitalMembership {
  hospitalId: string;
  role: 'ADMIN_GLOBAL' | 'ADMIN_HOPITAL' | 'MEDECIN' | 'SAGE_FEMME' | 'AIDE_SOIGNANT' | 'ECRAN';
  canOverrideLevel: boolean;
  canDischarge: boolean;
  canAdminHospital: boolean;
  displayMode: 'SALLE_ATTENTE' | 'SOIGNANTS' | null;
}

export interface CurrentUser {
  id: string;
  username: string;
  displayName: string;
  initials: string;
  isGlobalAdmin: boolean;
  hospitals: HospitalMembership[];
}

interface SessionResponse {
  accessToken: string;
  expiresIn: number;
  sessionEndsAt: string;
  user: CurrentUser;
}

const IDLE_LIMIT_MS = 30 * 60_000;
const IDLE_WARNING_MS = 2 * 60_000;

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);
  const sessionEndsAt = ref<Date | null>(null);
  const idleWarning = ref(false);
  const ready = ref(false);

  let lastActivity = Date.now();
  let idleTimer: number | null = null;
  let renewTimer: number | null = null;

  const isAuthenticated = computed(() => user.value !== null);

  /** Mode d'affichage figé, pour les comptes « écran ». */
  const displayMode = computed(() => user.value?.hospitals[0]?.displayMode ?? null);
  const isDisplayAccount = computed(() => user.value?.hospitals[0]?.role === 'ECRAN');

  function noteActivity(): void {
    lastActivity = Date.now();
    idleWarning.value = false;
  }

  function startTimers(expiresIn: number): void {
    stopTimers();

    // Renouvellement une minute avant l'expiration du jeton : une requête ne
    // doit jamais échouer pour cette raison en pleine saisie.
    renewTimer = window.setInterval(
      () => {
        void apiFetch<SessionResponse>('/api/auth/refresh', { method: 'POST', noRetry: true })
          .then((session) => {
            setAccessToken(session.accessToken);
            sessionEndsAt.value = new Date(session.sessionEndsAt);
          })
          .catch(() => {
            void logout();
          });
      },
      Math.max((expiresIn - 60) * 1000, 30_000),
    );

    idleTimer = window.setInterval(() => {
      const idleFor = Date.now() - lastActivity;
      idleWarning.value = idleFor > IDLE_LIMIT_MS - IDLE_WARNING_MS;
      if (idleFor > IDLE_LIMIT_MS) void logout();
    }, 15_000);

    for (const event of ['pointerdown', 'keydown', 'visibilitychange'] as const) {
      window.addEventListener(event, noteActivity, { passive: true });
    }
  }

  function stopTimers(): void {
    if (renewTimer !== null) window.clearInterval(renewTimer);
    if (idleTimer !== null) window.clearInterval(idleTimer);
    renewTimer = null;
    idleTimer = null;
    for (const event of ['pointerdown', 'keydown', 'visibilitychange'] as const) {
      window.removeEventListener(event, noteActivity);
    }
  }

  function adopt(session: SessionResponse): void {
    setAccessToken(session.accessToken);
    user.value = session.user;
    sessionEndsAt.value = new Date(session.sessionEndsAt);
    noteActivity();
    startTimers(session.expiresIn);
    connectRealtime();
  }

  async function login(username: string, password: string): Promise<void> {
    const session = await apiFetch<SessionResponse>('/api/auth/login', {
      method: 'POST',
      body: { username, password },
      noRetry: true,
    });
    adopt(session);
  }

  /** Au démarrage : le cookie permet peut-être de reprendre la session. */
  async function restore(): Promise<void> {
    try {
      const session = await apiFetch<SessionResponse>('/api/auth/refresh', {
        method: 'POST',
        noRetry: true,
      });
      adopt(session);
    } catch {
      user.value = null;
    } finally {
      ready.value = true;
    }
  }

  async function logout(): Promise<void> {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST', noRetry: true });
    } catch {
      // Se déconnecter localement doit réussir même si le serveur ne répond pas.
    }
    setAccessToken(null);
    user.value = null;
    sessionEndsAt.value = null;
    idleWarning.value = false;
    stopTimers();
    disconnectRealtime();
  }

  function membershipFor(hospitalId: string | null): HospitalMembership | null {
    if (hospitalId === null || user.value === null) return null;
    return user.value.hospitals.find((m) => m.hospitalId === hospitalId) ?? null;
  }

  onSessionLost(() => {
    user.value = null;
    stopTimers();
    disconnectRealtime();
  });

  return {
    user,
    sessionEndsAt,
    idleWarning,
    ready,
    isAuthenticated,
    displayMode,
    isDisplayAccount,
    login,
    logout,
    restore,
    membershipFor,
    noteActivity,
  };
});
